import { CustomLoader } from "@admin/components/ui/custom-loader";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Calendar,
  FolderOpen,
  ImageIcon,
  Layers,
  Tag,
  Video,
  Edit,
} from "lucide-react";
import type { DonationScheme } from "../../services/api";
import { parseDate } from "../../lib/utils";

function parseAmount(value?: string | number | null): number {
  if (value === "" || value === null) {
    return 0;
  }
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatInrFull(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateDisplay(value?: string | null) {
  if (!value) {
    return "—";
  }
  try {
    const d = parseDate(value);
    if (!d) return value;
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function formatDateRange(start?: string | null, end?: string | null) {
  if (!start && !end) {
    return null;
  }
  if (start && end) {
    return `${formatDateDisplay(start)} – ${formatDateDisplay(end)}`;
  }
  return formatDateDisplay(start ?? end);
}

function getCampaignStatusLabel(scheme: DonationScheme): string {
  if (scheme.date_status === "past") {
    return "Campaign ended";
  }
  if (scheme.date_status === "upcoming") {
    return "Upcoming campaign";
  }
  if (scheme.is_active === false) {
    return "Inactive";
  }
  return "Active campaign";
}

function getSchemeMediaUrls(
  scheme: DonationScheme,
  phase: "before" | "after"
): { url: string; isVideo: boolean }[] {
  const record = scheme as unknown as Record<string, unknown>;
  const raw =
    phase === "before"
      ? (scheme.before_image ??
        scheme.before_media ??
        record.before_images ??
        record.before_medias ??
        scheme.medias ??
        scheme.media ??
        record.donation_scheme_media ??
        record.media_files ??
        record.images)
      : (scheme.after_image ??
        scheme.after_media ??
        record.after_images ??
        record.after_medias);

  if (!raw) {
    return [];
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (Array.isArray(parsed)) {
          return parsed
            .map(item => {
              const url =
                typeof item === "string"
                  ? item
                  : String(
                      (item as Record<string, unknown>).url ??
                        (item as Record<string, unknown>).file ??
                        ""
                    );
              if (!url.trim()) {
                return null;
              }
              return {
                url: url.trim(),
                isVideo: /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url),
              };
            })
            .filter((x): x is { url: string; isVideo: boolean } => x !== null);
        }
      } catch {
        /* single URL string */
      }
    }
    return [
      {
        url: trimmed,
        isVideo: /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(trimmed),
      },
    ];
  }
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map(item => {
      if (typeof item === "string") {
        return {
          url: item,
          isVideo: /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(item),
        };
      }
      const row = item as Record<string, unknown>;
      const url = String(
        row.url ?? row.file ?? row.media ?? row.image ?? row.media_url ?? ""
      );
      if (!url) {
        return null;
      }
      return {
        url,
        isVideo: /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url),
      };
    })
    .filter((x): x is { url: string; isVideo: boolean } => x !== null);
}

function AboutThisCauseMetaItem({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Tag;
}) {
  return (
    <div className="flex gap-3 rounded-xl bg-muted/50 px-3 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="mt-0.5 text-sm font-medium text-primary">{value}</p>
      </div>
    </div>
  );
}

function CampaignPhaseMediaGrid({
  items,
  phase,
}: {
  items: { url: string; isVideo: boolean }[];
  phase: "before" | "after";
}) {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {items.map(item => (
        <div
          key={`${phase}-${item.isVideo ? "video" : "image"}-${item.url}`}
          className="relative aspect-video overflow-hidden rounded-lg border border-border bg-muted"
        >
          {item.isVideo ? (
            <video
              src={item.url}
              className="h-full w-full object-cover"
              controls
              muted
            />
          ) : (
            <img src={item.url} alt="" className="h-full w-full object-cover" />
          )}
          <span className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 rounded bg-background/90 px-1.5 py-0.5 text-[10px] text-foreground">
            {item.isVideo ? (
              <>
                <Video className="h-3 w-3" /> Video
              </>
            ) : (
              <>
                <ImageIcon className="h-3 w-3" /> Image
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function CampaignPhaseTabPanel({
  details,
  media,
  phase,
  emptyLabel,
}: {
  details?: string | null;
  media: { url: string; isVideo: boolean }[];
  phase: "before" | "after";
  emptyLabel: string;
}) {
  const hasDetails = Boolean(details?.trim());
  const hasMedia = media.length > 0;

  if (!hasDetails && !hasMedia) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-4">
      {hasDetails && (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {details}
        </p>
      )}
      {hasMedia && (
        <div className={hasDetails ? "border-t border-gray-100 pt-4" : ""}>
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Media
          </p>
          <CampaignPhaseMediaGrid items={media} phase={phase} />
        </div>
      )}
    </div>
  );
}

function SidebarSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </p>
      {children}
    </div>
  );
}

export interface DonationCampaignDetailsModalProps {
  open: boolean;
  loading: boolean;
  scheme: DonationScheme | null;
  donationTypeName: string;
  categoryName: string;
  onOpenChange: (open: boolean) => void;
  onEdit: (scheme: DonationScheme) => void;
}

export default function DonationCampaignDetailsModal({
  open,
  loading,
  scheme,
  donationTypeName,
  categoryName,
  onOpenChange,
  onEdit,
}: DonationCampaignDetailsModalProps) {
  const goal = scheme ? parseAmount(scheme.amount_needed) : 0;
  const raised = scheme ? parseAmount(scheme.amount_collected) : 0;
  const percent =
    goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  const stillNeeded = Math.max(0, goal - raised);
  const beforeMedia = scheme ? getSchemeMediaUrls(scheme, "before") : [];
  const afterMedia = scheme ? getSchemeMediaUrls(scheme, "after") : [];
  const bannerUrl =
    scheme?.banner_image?.trim() ?? scheme?.banner?.trim() ?? "";
  const dateRange = scheme
    ? formatDateRange(scheme.start_date, scheme.end_date)
    : null;
  const amountType = scheme?.amount_type === "variable" ? "Variable" : "Fixed";
  const hasBeforeContent = Boolean(
    scheme?.before_details?.trim() ?? beforeMedia.length > 0
  );
  const hasAfterContent = Boolean(
    scheme?.after_details?.trim() ?? afterMedia.length > 0
  );
  const showBeforeAfterTabs = hasBeforeContent || hasAfterContent;

  type MetaItem = { label: string; value: string; icon: typeof Tag };
  const metaItems: MetaItem[] = [];
  if (scheme) {
    if (donationTypeName !== "—") {
      metaItems.push({
        label: "Donation type",
        value: donationTypeName,
        icon: Tag,
      });
    }
    if (categoryName !== "—") {
      metaItems.push({
        label: "Category",
        value: categoryName,
        icon: FolderOpen,
      });
    }
    if (scheme.sub_category_title?.trim()) {
      metaItems.push({
        label: "Sub category",
        value: scheme.sub_category_title.trim(),
        icon: Layers,
      });
    } else if (
      scheme.sub_category !== null &&
      String(scheme.sub_category).trim()
    ) {
      metaItems.push({
        label: "Sub category",
        value: String(scheme.sub_category).trim(),
        icon: Layers,
      });
    }
    if (dateRange) {
      metaItems.push({
        label: "Campaign period",
        value: dateRange,
        icon: Calendar,
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92vh,900px)] w-full max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
        {loading || !scheme ? (
          <div className="flex h-64 flex-col items-center justify-center gap-4 bg-[#FDF7F7]">
            <CustomLoader size="md" />
            <p className="text-sm font-medium text-muted-foreground">
              Loading campaign details...
            </p>
          </div>
        ) : (
          <>
            <section className="shrink-0 border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5 pr-14">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">
                    {scheme.title}
                  </h2>
                  {scheme.sub_title?.trim() && (
                    <p className="mt-1 text-sm text-zinc-600">
                      {scheme.sub_title}
                    </p>
                  )}
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[#f0dede] bg-white px-3.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#8B3232]">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-[#8B3232]"
                    aria-hidden
                  />
                  {getCampaignStatusLabel(scheme)}
                </span>
              </div>

              {goal > 0 && (
                <div className="mt-5 space-y-4 rounded-lg border-t border-primary/20 px-5 py-3">
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                    <div className="min-w-0 space-y-1 sm:flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                        Amount raised
                      </p>
                      <p className="text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">
                        {formatInrFull(raised)}
                      </p>
                      <p className="text-xs text-slate-500">
                        of {formatInrFull(goal)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12">
                      {donationTypeName !== "—" && (
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Donation type
                          </p>
                          <p className="text-sm font-semibold text-zinc-900">
                            {donationTypeName}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="h-2 overflow-hidden rounded-full bg-zinc-200/90">
                      <div
                        className="h-full rounded-full bg-[#8B3232] transition-all duration-300"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="mt-2 flex justify-between text-xs font-medium text-slate-500">
                      <span>{percent}% funded</span>
                      <span className="text-zinc-700">
                        {formatInrFull(stillNeeded)} still needed
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </section>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-white px-6 py-5">
              {bannerUrl && (
                <div className="overflow-hidden rounded-lg border border-border">
                  <img
                    src={bannerUrl}
                    alt={`${scheme.title} banner`}
                    className="max-h-48 w-full object-cover"
                  />
                </div>
              )}
              {(scheme.description?.trim() ?? metaItems.length > 0) && (
                <SidebarSection title="About this cause">
                  {scheme.description?.trim() && (
                    <p className="text-sm leading-relaxed text-gray-700">
                      {scheme.description}
                    </p>
                  )}
                  {metaItems.length > 0 && (
                    <div
                      className={`grid gap-2.5 ${scheme.description?.trim() ? "mt-4 border-t border-gray-100 pt-4" : ""}`}
                    >
                      {metaItems.map(item => (
                        <AboutThisCauseMetaItem
                          key={item.label}
                          label={item.label}
                          value={item.value}
                          icon={item.icon}
                        />
                      ))}
                    </div>
                  )}
                </SidebarSection>
              )}

              <SidebarSection title="Campaign settings">
                <dl className="grid gap-3 sm:grid-cols-2">
                  <SettingItem
                    label="Status"
                    value={scheme.is_active !== false ? "Active" : "Inactive"}
                  />
                  <SettingItem label="Amount type" value={amountType} />
                  {amountType === "Fixed" && scheme.fixed_amount !== null && (
                    <SettingItem
                      label="Fixed amount"
                      value={formatInrFull(parseAmount(scheme.fixed_amount))}
                    />
                  )}
                  {amountType === "Variable" && (
                    <>
                      {scheme.min_contribution !== null && (
                        <SettingItem
                          label="Min contribution"
                          value={formatInrFull(
                            parseAmount(scheme.min_contribution)
                          )}
                        />
                      )}
                      {scheme.max_contribution !== null && (
                        <SettingItem
                          label="Max contribution"
                          value={formatInrFull(
                            parseAmount(scheme.max_contribution)
                          )}
                        />
                      )}
                    </>
                  )}
                  <SettingItem
                    label="Allow after target"
                    value={scheme.allow_donation_after_target ? "Yes" : "No"}
                  />
                  <SettingItem
                    label="Cash donation"
                    value={scheme.is_cash_donation !== false ? "Yes" : "No"}
                  />
                  {scheme.created_at && (
                    <SettingItem
                      label="Created"
                      value={formatDateDisplay(scheme.created_at)}
                    />
                  )}
                  {scheme.updated_at && (
                    <SettingItem
                      label="Updated"
                      value={formatDateDisplay(scheme.updated_at)}
                    />
                  )}
                </dl>
              </SidebarSection>

              {showBeforeAfterTabs && (
                <SidebarSection title="Before & After">
                  <Tabs
                    defaultValue={hasBeforeContent ? "before" : "after"}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="before">Before</TabsTrigger>
                      <TabsTrigger value="after">After</TabsTrigger>
                    </TabsList>
                    <TabsContent value="before" className="pt-3">
                      <CampaignPhaseTabPanel
                        details={scheme.before_details}
                        media={beforeMedia}
                        phase="before"
                        emptyLabel="No before details or media for this campaign."
                      />
                    </TabsContent>
                    <TabsContent value="after" className="pt-3">
                      <CampaignPhaseTabPanel
                        details={scheme.after_details}
                        media={afterMedia}
                        phase="after"
                        emptyLabel="No after details or media for this campaign."
                      />
                    </TabsContent>
                  </Tabs>
                </SidebarSection>
              )}
            </div>

            <DialogFooter className="shrink-0 gap-2 border-t bg-card px-6 py-4 sm:justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button className="gap-2" onClick={() => onEdit(scheme)}>
                <Edit className="h-4 w-4" />
                Edit Campaign
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SettingItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}
